import { NavLink } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  formatDate,
  IconDownload,
  IconFile,
  InputText,
  Select,
  IconLoader,
  SelectListPosition,
  IconChangeLoClock,
  PermissionEnum,
} from '@ui/src';
import { CustomerStatus } from '../../../../hoc/common/types';
import { useAppSelector } from '../../../../../../../store/use-store';
import { BeneficiaryStatus } from '../../../../hoc/common/beneficiaries/types';
import { ContractDetailsTypes } from '../types';

import cn from '../styles.module.css';

const BeneficiaryContractDetails = ({
  control,
  errors,
  beneficiaryContract,
  baseCurrency,
  beneficiaryName,
  documents,
  getFile,
  downloadId,
  downloadError,
  downloadInProgress,
  currencies,
  selectedCurrency,
  setSelectedCurrency,
  currenciesList,
  organizationStatus,
}: ContractDetailsTypes) => {
  const { activePermissions } = useAppSelector((state) => state.root);

  return (
    <>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="status"
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select status..."
              list={[
                {
                  name: 'Draft',
                  value: BeneficiaryStatus.DRAFT,
                  hidden:
                    beneficiaryContract?.status !== BeneficiaryStatus.DRAFT,
                },
                {
                  name: 'Under Review',
                  value: BeneficiaryStatus.UNDER_REVIEW,
                  hidden:
                    beneficiaryContract?.status === BeneficiaryStatus.APPROVED,
                },
                {
                  name: 'Approved',
                  value: BeneficiaryStatus.APPROVED,
                  hidden:
                    beneficiaryContract?.status === BeneficiaryStatus.DRAFT,
                },
                {
                  name: 'Rejected',
                  value: BeneficiaryStatus.REJECTED,
                  hidden:
                    beneficiaryContract?.status === BeneficiaryStatus.DRAFT,
                },
              ]}
              value={value}
              title="Status"
              onChange={onChange}
              disabled={
                beneficiaryContract?.status === 'REJECTED' ||
                organizationStatus === CustomerStatus.CLOSED
              }
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.contractDetailsTitle}>General Info</div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              title="Name"
              value={value}
              placeholder="Enter name..."
              onChange={onChange}
              error={errors?.name?.message}
              disabled={
                beneficiaryContract?.status === 'REJECTED' ||
                organizationStatus === CustomerStatus.CLOSED
              }
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="description"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              title="Description"
              value={value}
              placeholder="Enter description..."
              onChange={onChange}
              error={errors?.description?.message}
              disabled={
                beneficiaryContract?.status === 'REJECTED' ||
                organizationStatus === CustomerStatus.CLOSED
              }
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="currencyId"
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select currency"
              list={currencies}
              value={value}
              title="Currency"
              onChange={(e) => {
                onChange(e.target.value);

                setSelectedCurrency(
                  currenciesList?.find((item) => item.id === e.target.value),
                );
              }}
              disabled={
                beneficiaryContract?.status === 'REJECTED' ||
                organizationStatus === CustomerStatus.CLOSED
              }
              selectListPosition={SelectListPosition.TOP}
              fullWidth
            />
          )}
        />
      </div>
      <div className={`${cn.formGroup} ${cn.formGroupFlex}`}>
        <div className={cn.inputWrapper}>
          <Controller
            control={control}
            name="sum"
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                title="Amount"
                value={value}
                placeholder="Enter amount..."
                onChange={onChange}
                error={errors?.description?.message}
                currency={
                  currenciesList?.find(
                    (item) => item.id === selectedCurrency?.id,
                  ) || baseCurrency
                }
                disabled={
                  beneficiaryContract?.status === 'REJECTED' ||
                  organizationStatus === CustomerStatus.CLOSED
                }
                amountInput
              />
            )}
          />
        </div>
        <div className={cn.inputWrapper}>
          <InputText
            name="balance"
            title="Remaining amount"
            value={
              Number(beneficiaryContract?.sum) -
              Number(beneficiaryContract?.transactionsSum)
            }
            placeholder=""
            currency={baseCurrency}
            disabled
            amountInput
          />
        </div>
      </div>
      <div className={cn.formGroup}>
        <InputText
          name="beneficiaryName"
          title="Beneficiary"
          value={beneficiaryName}
          placeholder=""
          disabled
        />
      </div>
      <div className={cn.contractDetailsTitle}>Documents</div>
      {documents &&
        documents.length > 0 &&
        documents.map((item) => (
          <div key={item.id} className={cn.document}>
            <div className={cn.documentLeft}>
              <div className={cn.documentLeftTop}>
                <IconFile width={16} height={16} />
                {item.name || 'Document'}
              </div>
              <div className={cn.documentDate}>
                {formatDate(item.createdAt, true)}
              </div>
            </div>
            <button
              type="button"
              className={cn.downloadButton}
              aria-label="Download button"
              onClick={() => getFile(item.id)}
            >
              {downloadId === item.id && downloadInProgress ? (
                <IconLoader width={16} height={16} />
              ) : (
                <IconDownload />
              )}
            </button>
            {downloadError && (
              <div className={cn.downloadError}>{downloadError}</div>
            )}
          </div>
        ))}
      {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
        <NavLink
          to={`/administration/change-logs?entityType=BENEFICIARY_CONTRACT&entityId=${beneficiaryContract?.id}`}
          target="_blank"
          className={cn.changeLogLink}
        >
          <IconChangeLoClock />
          Change Log
        </NavLink>
      )}
    </>
  );
};

export default BeneficiaryContractDetails;
